import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import type { FC, JSX, ReactNode } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';
import type { MenuTreeNode } from './menuTree';
import { navigationIncludesCurrentPath } from './utils/navigationIncludesCurrentPath';

interface Props {
  index: number;
  item: MenuTreeNode;
  pathOpen: number[];
  path: number[];
  compact: boolean;
  submenu: ReactNode;
  toggleMenu: (path: number[]) => void;
  label: JSX.Element;
  visiblePath?: number[];
  setVisiblePath: (visibility?: number[]) => void;
}

export const NavigationSubmenu: FC<Props> = (props) => {
  const {
    index,
    item,
    pathOpen,
    path,
    submenu,
    label,
    toggleMenu,
    visiblePath,
    setVisiblePath,
  } = props;

  const currentPath = [...path, index];

  const scrollRef = useRef<Scrollbars>(null);

  const [maxHeight, setMaxHeight] = useState<number>();
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.compact): intentional reaction on change
  // biome-ignore lint/correctness/useExhaustiveDependencies(visiblePath): intentional reaction on change
  useEffect(() => {
    if (scrollRef.current?.container) {
      const submenuTop =
        scrollRef.current.container.getBoundingClientRect().top;

      setMaxHeight(window.innerHeight - submenuTop);
    }
  }, [props.compact, visiblePath]);

  const location = useLocation();

  const [openIndex] = pathOpen;
  const firstLevelCompact = props.compact && currentPath.length === 1;
  const firstLevel = currentPath.length === 1;
  const navigationItemProps = firstLevelCompact
    ? {}
    : {
        onClick: () => {
          toggleMenu(currentPath);
        },
      };
  return (
    <li key={index}>
      <div
        className={classNames(
          'navigation__item',
          (navigationIncludesCurrentPath(item, location) ||
            index === openIndex) &&
            'navigation__item--active',
        )}
        {...navigationItemProps}
        onMouseEnter={() => {
          if (firstLevelCompact) {
            setVisiblePath(currentPath);
          }
        }}
        onMouseLeave={() => {
          if (firstLevelCompact) {
            setVisiblePath(undefined);
          }
        }}
      >
        {label}
        {!firstLevel && (
          <div className='navigation__item-arrow'>
            <Icon
              glyph={openIndex === index ? 'fa-chevron-up' : 'fa-chevron-down'}
              size='xs'
              className='mr-2'
            />
          </div>
        )}
      </div>
      {firstLevelCompact &&
        visiblePath &&
        isEqual(visiblePath, currentPath) && (
          <ScrollView
            className='navigation__scrollbox'
            ref={scrollRef}
            autoHeight
            autoHeightMax={maxHeight}
          >
            <ul
              onMouseEnter={() => {
                setVisiblePath(currentPath);
              }}
              onMouseLeave={() => {
                setVisiblePath(undefined);
              }}
            >
              {submenu}
            </ul>
          </ScrollView>
        )}
      {!firstLevelCompact && index === openIndex && <ul>{submenu}</ul>}
    </li>
  );
};
